// Bitrix-Verbindungspflicht - Anbindung an die gemeinsame Verbindungsseite.
//
// Die Verbindung selbst liegt an genau einer Stelle (projekte.en-power.eu), weil
// Bitrix je Person nur ein Auffrischmerkmal ausgibt: Acht eigene Anbindungen
// wuerden sich gegenseitig abschiessen. Dieses Werkzeug fragt deshalb nur nach
// und verweist dorthin - es haelt keine eigenen Bitrix-Zugangsdaten.

export const VERBINDUNGSSEITE = 'https://projekte.en-power.eu/verbinden';

// Ein Merker je Browsersitzung. Ohne ihn pendelt jemand, der auf der
// Verbindungsseite abbricht, zwischen Werkzeug und Verbindungsseite.
const UMLEITUNGS_MERKER = 'enpower.bitrixVerbindung.umgeleitet.v1';

export const VERBINDUNG_UNBEKANNT = Object.freeze({
  geprueft: false,
  mussVerbinden: false,
  grund: 'nicht_geprueft',
  pflichtAktiv: false,
});

function liesMerker() {
  try {
    return window.sessionStorage.getItem(UMLEITUNGS_MERKER) === '1';
  } catch (fehler) {
    // Speicher gesperrt (privates Fenster, Einbettung): dann lieber gar nicht
    // umleiten, als in einer Schleife zu landen.
    return true;
  }
}

function setzeMerker() {
  try {
    window.sessionStorage.setItem(UMLEITUNGS_MERKER, '1');
  } catch (fehler) {
    /* nicht schlimm - die Umleitung findet dann eben nur dieses eine Mal statt */
  }
}

/**
 * Fragt die gemeinsame Auth-Datenbank, ob die angemeldete Person verbunden ist.
 *
 * Bei einer Stoerung wird durchgelassen - ein defekter Tuersteher soll die Tuer
 * oeffnen, nicht verriegeln. Das Ergebnis traegt dann aber `geprueft: false`,
 * damit "nicht geprueft" nicht wie "bestanden" aussieht.
 */
export async function pruefeBitrixVerbindung(supabase) {
  if (!supabase) {
    console.warn('[verbindungspflicht] nicht geprueft - kein Supabase-Client vorhanden.');
    return VERBINDUNG_UNBEKANNT;
  }

  const { data, error } = await supabase.rpc('bitrix_verbindung_pflicht_status');

  if (error) {
    console.warn('[verbindungspflicht] nicht geprueft - Abfrage fehlgeschlagen:', error.message);
    return VERBINDUNG_UNBEKANNT;
  }

  const zeile = Array.isArray(data) ? data[0] : data;

  if (!zeile) {
    console.warn('[verbindungspflicht] nicht geprueft - die Abfrage lieferte keine Zeile.');
    return VERBINDUNG_UNBEKANNT;
  }

  // PostgREST liefert ein Array; wer das uebersieht, liest `erfuellt` vom Array
  // selbst und bekommt `undefined`. `undefined !== false` heisst dann
  // stillschweigend "durchlassen" - richtig aus dem falschen Grund. Deshalb
  // gilt alles, was kein Wahrheitswert ist, ausdruecklich als ungeprueft.
  if (typeof zeile.erfuellt !== 'boolean') {
    console.warn('[verbindungspflicht] nicht geprueft - die Antwort enthaelt kein Feld "erfuellt".');
    return VERBINDUNG_UNBEKANNT;
  }

  // Auf BEIDEN Feldern entscheiden: Bei ruhender Pflicht ist eine Umleitung nie
  // richtig, auch wenn `erfuellt` aus einem anderen Grund false meldet.
  const pflichtAktiv = zeile.pflicht_aktiv === true;
  const grund = String(zeile.grund || '');

  // "nicht_angemeldet" heisst nicht "nicht verbunden", sondern "die Datenbank
  // kennt gerade keine Sitzung" - typischerweise, weil das Token mitten in der
  // Arbeit abgelaufen ist. Seit die Pflicht scharf ist, kommt dieser Fall als
  // pflicht_aktiv=true / erfuellt=false zurueck und saehe ohne diese Zeile aus
  // wie ein Verbindungsmangel. Die Verbindungsseite koennte ihn nicht loesen.
  const sitzungFehlt = grund === 'nicht_angemeldet';

  if (sitzungFehlt) {
    console.warn('[verbindungspflicht] nicht geprueft - die Datenbank sieht keine Sitzung (Token abgelaufen?).');
  }

  return {
    geprueft: !sitzungFehlt,
    mussVerbinden: pflichtAktiv && zeile.erfuellt === false && !sitzungFehlt,
    grund,
    pflichtAktiv,
  };
}

/**
 * Baut die Zieladresse der Verbindungsseite mit kodierter Rueckadresse.
 *
 * Die Kodierung ist Pflicht: Ohne sie schneidet die Gegenseite bei einer
 * Herkunft mit mehreren Parametern alles ab dem ersten `&` ab - der Fehler
 * faellt hier nicht auf, sondern erst drueben als Sackgasse.
 */
export function baueVerbindungsadresse(herkunft) {
  const zurueck = String(herkunft || '');
  return `${VERBINDUNGSSEITE}?zurueck=${encodeURIComponent(zurueck)}`;
}

/**
 * Schickt die Person einmal je Browsersitzung zur Verbindungsseite.
 * Rueckgabe: true, wenn tatsaechlich umgeleitet wurde.
 */
export function leiteZurVerbindungsseite(herkunft = window.location.href) {
  if (liesMerker()) {
    return false;
  }

  setzeMerker();
  window.location.href = baueVerbindungsadresse(herkunft);
  return true;
}
